import { Fragment } from "preact"
import { useEffect, useMemo, useState } from "preact/hooks"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { SearchableSelect, SearchableSelectOption } from "@/components/searchable_select"
import { Loader2, Plus } from "lucide-react"
import {
  CustomerPricingHistoryEntry,
  CustomerPricingRow,
  deleteCustomerPrice,
  formatPricingDate,
  formatUnitPrice,
  listCustomerPricingCustomers,
  listCustomerPricingHistory,
  searchCustomerPricing,
  setCustomerPricing,
} from "@/api/customer_pricing"
import { ProductLine, ProductSearchResult, listProductLines, searchProducts } from "@/api/product_catalog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"

const PAGE_SIZE = 50

interface ExpandedPricingKey {
  product_id: string
  customer_name: string
  retailer_name?: string
}

function pricingRowKey(row: CustomerPricingRow): string {
  return `${row.product_id}:${row.customer_name}:${row.retailer_name || ""}`
}

function expandedKeyToString(key: ExpandedPricingKey): string {
  return `${key.product_id}:${key.customer_name}:${key.retailer_name || ""}`
}

export function CustomerPricingSettings() {
  const [search, setSearch] = useState("")
  const [customerFilter, setCustomerFilter] = useState("")
  const [productFilter, setProductFilter] = useState("")
  const [rows, setRows] = useState<CustomerPricingRow[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [customers, setCustomers] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const [historyByKey, setHistoryByKey] = useState<Record<string, CustomerPricingHistoryEntry[]>>({})
  const [historyLoadingKey, setHistoryLoadingKey] = useState<string | null>(null)
  const [setPriceOpen, setSetPriceOpen] = useState(false)
  const [setPricePrefill, setSetPricePrefill] = useState<SetPricePrefill | null>(null)

  const customerOptions = useMemo((): SearchableSelectOption[] => {
    return [
      { value: "", label: "All customers" },
      ...customers.map((customer) => ({ value: customer, label: customer })),
    ]
  }, [customers])

  useEffect(() => {
    void loadCustomers()
  }, [])

  useEffect(() => {
    setOffset(0)
  }, [search, customerFilter, productFilter])

  useEffect(() => {
    void loadPricing()
  }, [search, customerFilter, productFilter, offset])

  async function loadCustomers() {
    const response = await listCustomerPricingCustomers()
    if (response.ok) {
      setCustomers(response.value.data)
    }
  }

  async function loadPricing() {
    setLoading(true)
    try {
      const response = await searchCustomerPricing({
        q: search.trim() || undefined,
        customer: customerFilter.trim() || undefined,
        product: productFilter.trim() || undefined,
        limit: PAGE_SIZE,
        offset,
      })
      if (response.ok) {
        setRows(response.value.data.items)
        setTotal(response.value.data.total)
      }
    } finally {
      setLoading(false)
    }
  }

  async function toggleHistory(row: CustomerPricingRow) {
    const key = pricingRowKey(row)
    if (expandedKey === key) {
      setExpandedKey(null)
      return
    }
    setExpandedKey(key)
    if (historyByKey[key]) {
      return
    }
    setHistoryLoadingKey(key)
    try {
      const response = await listCustomerPricingHistory({
        product_id: row.product_id,
        customer_name: row.customer_name,
        retailer_name: row.retailer_name,
      })
      if (response.ok) {
        setHistoryByKey((current) => ({ ...current, [key]: response.value.data }))
      }
    } finally {
      setHistoryLoadingKey(null)
    }
  }

  const pageStart = total === 0 ? 0 : offset + 1
  const pageEnd = Math.min(offset + PAGE_SIZE, total)
  const canPrev = offset > 0
  const canNext = offset + PAGE_SIZE < total

  return (
    <div class="space-y-4">
      <div class="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        <p class="font-medium">Customer pricing</p>
        <p class="mt-1 text-xs">
          Browse current case prices by customer and product. Price changes from earlier effective dates only appear when
          you expand a row.
        </p>
      </div>

      <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
        <FilterInput label="Search" value={search} onChange={setSearch} placeholder="Customer, retailer, or product" />
        <div>
          <span class="mb-1 block text-sm font-medium text-gray-700">Customer</span>
          <SearchableSelect
            options={customerOptions}
            value={customerFilter}
            onValueChange={setCustomerFilter}
            placeholder="All customers"
            searchPlaceholder="Search customers..."
            emptyMessage="No customers found."
          />
        </div>
        <FilterInput label="Product" value={productFilter} onChange={setProductFilter} placeholder="Product name or SKU" />
      </div>

      <div class="flex flex-wrap items-center justify-between gap-3">
        <p class="text-sm text-gray-600">
          {loading ? "Loading pricing..." : total === 0 ? "No current prices" : `Showing ${pageStart}–${pageEnd} of ${total}`}
        </p>
        <div class="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={!canPrev || loading} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
            Previous
          </Button>
          <Button variant="outline" size="sm" disabled={!canNext || loading} onClick={() => setOffset(offset + PAGE_SIZE)}>
            Next
          </Button>
          <Button variant="outline" onClick={() => void Promise.all([loadPricing(), loadCustomers()])} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Refreshing...
              </>
            ) : (
              "Refresh"
            )}
          </Button>
          <Button
            onClick={() => {
              setSetPricePrefill(null)
              setSetPriceOpen(true)
            }}
          >
            <Plus className="h-4 w-4 mr-1" />
            Set price
          </Button>
        </div>
      </div>

      <div class="overflow-hidden rounded-md border border-gray-200">
        {rows.length === 0 ? (
          <div class="p-4 text-sm text-gray-500">{loading ? "Loading..." : "No customer pricing found."}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead class="px-3">Customer</TableHead>
                <TableHead class="px-3">Product</TableHead>
                <TableHead class="px-3">Price</TableHead>
                <TableHead class="px-3">Effective</TableHead>
                <TableHead class="px-3 w-28" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const key = pricingRowKey(row)
                const isExpanded = expandedKey === key
                const history = historyByKey[key] || []
                const differentOlderCount = row.different_older_count
                return (
                  <Fragment key={key}>
                    <TableRow class={isExpanded ? "bg-blue-50/60" : ""}>
                      <TableCell class="px-3 py-2 align-top">
                        <div class="font-medium text-gray-900">{row.customer_name}</div>
                        {row.retailer_name ? <div class="text-xs text-gray-500">via {row.retailer_name}</div> : null}
                      </TableCell>
                      <TableCell class="px-3 py-2 align-top">
                        <div class="font-medium text-gray-900">{row.product_name}</div>
                        {row.product_sku ? <div class="text-xs text-gray-500">{row.product_sku}</div> : null}
                      </TableCell>
                      <TableCell class="px-3 py-2 align-top text-sm text-gray-900">
                        {formatUnitPrice(row.unit_price, row.currency)}
                        {row.price_type ? <div class="text-xs text-gray-500 capitalize">{row.price_type}</div> : null}
                      </TableCell>
                      <TableCell class="px-3 py-2 align-top text-xs text-gray-600">
                        {formatPricingDate(row.effective_start)} – {formatPricingDate(row.effective_end)}
                      </TableCell>
                      <TableCell class="px-3 py-2 align-top">
                        <div class="flex items-center gap-1.5">
                          <Button
                            variant="outline"
                            class="h-8 text-xs"
                            onClick={() => {
                              setSetPricePrefill({
                                customer_name: row.customer_name,
                                retailer_name: row.retailer_name,
                                product: { id: row.product_id, name: row.product_name },
                                unit_price: row.unit_price,
                                price_id: row.price_id,
                              })
                              setSetPriceOpen(true)
                            }}
                          >
                            Edit
                          </Button>
                          {differentOlderCount > 0 ? (
                            <Button variant="outline" class="h-8 text-xs" onClick={() => void toggleHistory(row)}>
                              {historyLoadingKey === key
                                ? "Loading..."
                                : isExpanded
                                  ? "Hide history"
                                  : `${differentOlderCount} older`}
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                    {isExpanded ? (
                      <TableRow key={`${key}-history`}>
                        <TableCell colSpan={5} class="bg-gray-50 px-3 py-3">
                          <PriceHistoryPanel history={history} loading={historyLoadingKey === key} currentPrice={row.unit_price} />
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <SetPriceDialog
        open={setPriceOpen}
        prefill={setPricePrefill}
        customers={customers}
        onClose={() => setSetPriceOpen(false)}
        onSaved={() => {
          setSetPriceOpen(false)
          setHistoryByKey({})
          void Promise.all([loadPricing(), loadCustomers()])
        }}
      />
    </div>
  )
}

interface SetPricePrefill {
  customer_name: string
  retailer_name?: string
  product: { id: string; name: string }
  unit_price: number
  /** Present when editing an existing row (enables delete). */
  price_id?: string
}

interface SetPriceDialogProps {
  open: boolean
  prefill: SetPricePrefill | null
  customers: string[]
  onClose: () => void
  onSaved: () => void
}

/**
 * One price for a customer across a whole product line (pack format) or a single product.
 * Line pricing is the default: brands price by pack format, so a 6-pack price should be
 * typed once, not once per flavor. An effective date makes the change apply from that day —
 * the old price is closed the day before and stays in history.
 */
function SetPriceDialog({ open, prefill, customers, onClose, onSaved }: SetPriceDialogProps) {
  const [customerName, setCustomerName] = useState("")
  const [scope, setScope] = useState<"line" | "product">("line")
  const [lines, setLines] = useState<ProductLine[]>([])
  const [lineId, setLineId] = useState("")
  const [productQuery, setProductQuery] = useState("")
  const [productOptions, setProductOptions] = useState<ProductSearchResult[]>([])
  const [productId, setProductId] = useState("")
  const [price, setPrice] = useState("")
  const [effectiveStart, setEffectiveStart] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    setSaving(false)
    if (prefill) {
      setCustomerName(prefill.customer_name)
      setScope("product")
      setProductId(prefill.product.id)
      setProductQuery(prefill.product.name)
      setPrice(String(prefill.unit_price))
    } else {
      setCustomerName("")
      setScope("line")
      setLineId("")
      setProductId("")
      setProductQuery("")
      setPrice("")
    }
    setEffectiveStart("")
    void loadLines()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill])

  async function loadLines() {
    const response = await listProductLines()
    if (response.ok) {
      setLines(response.value.data)
    }
  }

  useEffect(() => {
    if (scope !== "product" || !open) return
    const handle = setTimeout(() => {
      void (async () => {
        const response = await searchProducts(productQuery.trim(), 25)
        if (response.ok) {
          setProductOptions(response.value.data)
        }
      })()
    }, 250)
    return () => clearTimeout(handle)
  }, [productQuery, scope, open])

  const customerOptions = useMemo((): SearchableSelectOption[] => {
    const known = customers.map((customer) => ({ value: customer, label: customer }))
    const trimmed = customerName.trim()
    if (trimmed && !customers.some((customer) => customer.toLowerCase() === trimmed.toLowerCase())) {
      return [{ value: trimmed, label: `${trimmed} (new)` }, ...known]
    }
    return known
  }, [customers, customerName])

  const parsedPrice = Number(price)
  const canSave =
    customerName.trim().length > 0 &&
    Number.isFinite(parsedPrice) &&
    parsedPrice > 0 &&
    (scope === "line" ? lineId.length > 0 : productId.length > 0) &&
    !saving

  async function handleSave() {
    if (!canSave) return
    setSaving(true)
    setError(null)
    const response = await setCustomerPricing({
      customer_name: customerName.trim(),
      // Editing a retailer-scoped row must keep its retailer, or the server matches nothing
      // and inserts a retailer-less duplicate beside the row being edited.
      retailer_name: prefill?.retailer_name,
      product_line_id: scope === "line" ? lineId : undefined,
      product_ids: scope === "product" ? [productId] : undefined,
      // Money in cents first to dodge float dust from the input.
      unit_price: Math.round(parsedPrice * 100) / 100,
      effective_start: effectiveStart || undefined,
    })
    setSaving(false)
    if (response.ok) {
      onSaved()
    } else {
      setError("Failed to save the price. Check the values and try again.")
    }
  }

  async function handleDelete() {
    if (!prefill?.price_id) return
    if (!window.confirm(`Delete the current ${prefill.product.name} price for ${prefill.customer_name}?`)) return
    setSaving(true)
    const response = await deleteCustomerPrice(prefill.price_id)
    setSaving(false)
    if (response.ok) {
      onSaved()
    } else {
      setError("Failed to delete the price.")
    }
  }

  return (
    <Dialog open={open} onOpenChange={(value: boolean) => (!value ? onClose() : null)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{prefill ? "Edit price" : "Set price"}</DialogTitle>
        </DialogHeader>
        <div class="space-y-4">
          <div>
            <Label class="mb-1 block text-sm">Customer</Label>
            <SearchableSelect
              options={customerOptions}
              value={customerName}
              onValueChange={setCustomerName}
              onSearchChange={setCustomerName}
              placeholder="Pick or type a customer"
              searchPlaceholder="Type a customer name..."
              emptyMessage="Keep typing to add a new customer."
            />
            <p class="mt-1 text-xs text-gray-500">Typing a name that isn't listed creates it.</p>
          </div>

          {!prefill ? (
            <div>
              <Label class="mb-1 block text-sm">Applies to</Label>
              <div class="flex gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  variant={scope === "line" ? "default" : "outline"}
                  onClick={() => setScope("line")}
                >
                  Whole product line
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={scope === "product" ? "default" : "outline"}
                  onClick={() => setScope("product")}
                >
                  Single product
                </Button>
              </div>
            </div>
          ) : null}

          {scope === "line" ? (
            <div>
              <Label class="mb-1 block text-sm">Product line</Label>
              <SearchableSelect
                options={lines.map((line) => ({ value: line.id, label: line.name }))}
                value={lineId}
                onValueChange={setLineId}
                placeholder="Pick a product line"
                searchPlaceholder="Search lines..."
                emptyMessage="No product lines."
              />
              <p class="mt-1 text-xs text-gray-500">
                One case price for every product in the line — no more typing it per flavor.
              </p>
            </div>
          ) : (
            <div>
              <Label class="mb-1 block text-sm">Product</Label>
              <SearchableSelect
                options={productOptions.map((product) => ({ value: product.id, label: product.name }))}
                value={productId}
                onValueChange={(value: string) => {
                  setProductId(value)
                  const match = productOptions.find((product) => product.id === value)
                  if (match) setProductQuery(match.name)
                }}
                onSearchChange={setProductQuery}
                placeholder={prefill ? prefill.product.name : "Search the catalog"}
                searchPlaceholder="Search products..."
                emptyMessage="No catalog products match."
              />
            </div>
          )}

          <div class="grid grid-cols-2 gap-3">
            <div>
              <Label class="mb-1 block text-sm">Case price</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={price}
                onInput={(event) => setPrice(event.currentTarget.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label class="mb-1 block text-sm">Effective from (optional)</Label>
              <Input
                type="date"
                value={effectiveStart}
                onInput={(event) => setEffectiveStart(event.currentTarget.value)}
              />
              <p class="mt-1 text-xs text-gray-500">
                {effectiveStart
                  ? "The old price stays on history and ends the day before."
                  : "Blank = replace the current price."}
              </p>
            </div>
          </div>

          {error ? <p class="text-sm text-red-600">{error}</p> : null}
        </div>
        <DialogFooter>
          {prefill?.price_id ? (
            <Button type="button" variant="outline" class="mr-auto text-red-600" disabled={saving} onClick={() => void handleDelete()}>
              Delete
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? "Saving..." : "Save price"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface FilterInputProps {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder: string
}

function FilterInput({ label, value, onChange, placeholder }: FilterInputProps) {
  return (
    <label class="block">
      <span class="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      <input
        value={value}
        onInput={(event) => onChange(event.currentTarget.value)}
        placeholder={placeholder}
        class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </label>
  )
}

interface PriceHistoryPanelProps {
  history: CustomerPricingHistoryEntry[]
  loading: boolean
  currentPrice: number
}

function PriceHistoryPanel({ history, loading, currentPrice }: PriceHistoryPanelProps) {
  if (loading) {
    return <div class="text-sm text-gray-500">Loading price history...</div>
  }
  const differentHistory = history.filter((entry) => entry.unit_price !== currentPrice)
  if (differentHistory.length === 0) {
    return <div class="text-sm text-gray-500">No prior prices with a different amount.</div>
  }
  return (
    <div class="space-y-2">
      <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">Prior prices</p>
      <div class="divide-y divide-gray-200 rounded-md border border-gray-200 bg-white">
        {differentHistory.map((entry, index) => (
          <div key={`${entry.effective_start || "open"}-${index}`} class="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
            <div>
              <span class="font-medium text-gray-900">{formatUnitPrice(entry.unit_price, entry.currency)}</span>
              {entry.price_type ? <span class="ml-2 text-xs text-gray-500 capitalize">{entry.price_type}</span> : null}
            </div>
            <div class="text-xs text-gray-600">
              {formatPricingDate(entry.effective_start)} – {formatPricingDate(entry.effective_end)}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("pricingRowKey", () => {
    it("includes retailer in the key", () => {
      expect(
        pricingRowKey({
          price_id: "cp1",
          product_id: "p1",
          product_name: "Lamb Saag 10oz",
          customer_name: "C&S",
          retailer_name: "Stop & Shop",
          unit_price: 29.2,
          currency: "USD",
          different_older_count: 2,
        })
      ).toBe("p1:C&S:Stop & Shop")
    })
  })

  describe("expandedKeyToString", () => {
    it("matches pricingRowKey format", () => {
      const key: ExpandedPricingKey = {
        product_id: "p1",
        customer_name: "HyVee",
      }
      expect(expandedKeyToString(key)).toBe("p1:HyVee:")
    })
  })
}
